import type { TileEnquiryConfig } from '~/modules/enquiry/hooks/use-tile-enquiry';
import type {
    TileActionInfo,
    TileVisitWebsiteConfig,
} from '~/modules/moodboards/components/TileActionPanel';
import type { ShareOptions } from '~/modules/social-sharing/utils/sharing';
import type { DetailTile } from './TileCarousel';

export interface DetailTileActions {
    enquiry: TileEnquiryConfig | null;
    share: ShareOptions | null;
    tileInfo: TileActionInfo;
    visitWebsite: TileVisitWebsiteConfig | null;
}

/**
 * "Visit site" for a detail tile, when the surface fetched the item's website.
 *
 * Null without one, which is how a surface whose query does not ask for the
 * URL keeps the action out of the panel rather than showing a dead button.
 * A professional's tile points at its own site; a product or project points at
 * its supplier's, attributed to that professional on the `Website` event.
 */
const tileVisitWebsiteFor = (
    tile: DetailTile,
): TileVisitWebsiteConfig | null => {
    if (!tile.website) return null;
    if (tile.pinType === 'ProfessionalPin') {
        return {
            website: tile.website,
            libraryItemID: tile.id,
            professionalID: tile.id,
            professionalTitle: tile.title,
        };
    }
    return {
        website: tile.website,
        libraryItemID: tile.id,
        professionalID: tile.professional?.id ?? 0,
        professionalTitle: tile.professional?.name ?? '',
    };
};

/**
 * Enquiry config for a detail tile's action panel.
 *
 * Professional tiles enquire about themselves. Product and project tiles
 * enquire their professional about the item. An article enquires the
 * professional who wrote it, about them rather than about the article — there
 * is nothing to quote on a piece of writing, but a reader who has just read one
 * has a reason to talk to whoever published it.
 *
 * All three of the latter need `professional.id` + `professionalLink` on the
 * tile: without the link the panel cannot resolve whether this professional
 * takes enquiries from this visitor, so it offers none rather than one that
 * fails on click.
 */
const tileEnquiryFor = (tile: DetailTile): TileEnquiryConfig | null => {
    if (tile.pinType === 'ProfessionalPin') {
        return {
            itemType: 'professional',
            siteTreeID: tile.id,
            professionalId: tile.id,
            professionalName: tile.title,
            professionalLink: tile.link,
            itemTitle: tile.title,
        };
    }
    if (!tile.professional?.id || !tile.professionalLink) return null;

    if (tile.pinType === 'ArticlePin') {
        return {
            itemType: 'professional',
            siteTreeID: tile.professional.id,
            professionalId: tile.professional.id,
            professionalName: tile.professional.name ?? '',
            professionalLink: tile.professionalLink,
            itemTitle: tile.professional.name ?? '',
        };
    }

    const itemType =
        tile.pinType === 'ProductPin'
            ? ('product' as const)
            : tile.pinType === 'ProjectPin'
              ? ('project' as const)
              : null;
    if (!itemType) return null;

    return {
        itemType,
        siteTreeID: tile.id,
        professionalId: tile.professional.id,
        professionalName: tile.professional.name ?? '',
        professionalLink: tile.professionalLink,
        itemTitle: tile.title,
        itemImage: tile.images?.[0]?.src,
    };
};

/**
 * The action panel config (enquiry / share / mobile sheet header) for a
 * DetailTile-shaped tile.
 * Shared by {@link DetailTileCard} and the sections that compose
 * {@link DetailTileMedia} directly.
 */
export const detailTileActionsFor = (tile: DetailTile): DetailTileActions => ({
    enquiry: tile.pinType ? tileEnquiryFor(tile) : null,
    share: tile.link ? { url: tile.link, title: tile.title } : null,
    tileInfo: {
        title: tile.title,
        subtitle: tile.subtitle ?? tile.professional?.name,
        image: tile.logo ?? tile.images?.[0]?.src,
        imageShape: tile.logo ? ('circle' as const) : ('square' as const),
        imageBackground: tile.logo
            ? (tile.logoBackground ?? undefined)
            : undefined,
    },
    visitWebsite: tileVisitWebsiteFor(tile),
});
